import { useEffect, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import { Plus, Trash, ArrowsClockwise, CheckCircle } from "@phosphor-icons/react";

const blankQ = () => ({ id: Math.random().toString(36).slice(2, 10), question: "", options: ["", "", "", ""], answer: 0, explanation: "" });
const blankM = () => ({ id: Math.random().toString(36).slice(2, 10), title: "", content: "", key_points: [] });

/** Review / edit an AI-written course: settings, modules, quiz with the correct answer marked, then publish. */
export default function CourseEditorDialog({ courseId, open, onOpenChange, onSaved }) {
  const [c, setC] = useState(null);
  const [tab, setTab] = useState("modules");
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const pending = c?.ai?.status === "pending";

  useEffect(() => {
    if (!open || !courseId) return;
    setC(null); setDirty(false);
    API.get(`/hse/training/courses/${courseId}`).then(({ data }) => setC(data)).catch(() => {});
  }, [open, courseId]);
  useEffect(() => {
    if (!open || !courseId || !pending) return;
    const t = setInterval(() => API.get(`/hse/training/courses/${courseId}`).then(({ data }) => { if (data.ai?.status !== "pending") { setC(data); setDirty(false); } }).catch(() => {}), 4000);
    return () => clearInterval(t);
  }, [open, courseId, pending]);

  if (!c) return null;
  const set = (patch) => { setDirty(true); setC(x => ({ ...x, ...patch })); };
  const setM = (i, patch) => set({ modules: c.modules.map((m, j) => (j === i ? { ...m, ...patch } : m)) });
  const setQ = (i, patch) => set({ quiz: c.quiz.map((q, j) => (j === i ? { ...q, ...patch } : q)) });

  const save = async (status) => {
    setBusy(true);
    try {
      const body = { title: c.title, blurb: c.blurb, audience: c.audience, pass_mark: c.pass_mark, validity_months: c.validity_months, duration_minutes: c.duration_minutes, modules: c.modules, quiz: c.quiz, ...(status ? { status } : {}) };
      const { data } = await API.put(`/hse/training/courses/${courseId}`, body);
      setC(data); setDirty(false); toast.success(status === "published" ? "Course published — you can now assign it" : "Saved"); onSaved?.(data);
      if (status) onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const regenerate = async () => {
    const brief = window.prompt("Anything to change this time? (optional)", "");
    if (brief === null) return;
    try { await API.post(`/hse/training/courses/${courseId}/regenerate`, { brief }); setC(x => ({ ...x, ai: { ...x.ai, status: "pending" } })); setDirty(false); toast.success("Rewriting…"); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="course-editor-dialog">
        <DialogHeader>
          <div className="flex items-start gap-2 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <Input value={c.title} onChange={e => set({ title: e.target.value })} className="h-9 font-semibold" data-testid="course-editor-title" />
              <p className="text-[11px] text-muted-foreground mt-1">{c.topic_label} · {c.module_count} modules · {c.question_count} questions · ~{c.duration_minutes} min</p>
            </div>
            <Badge variant="outline" className="capitalize" data-testid="course-editor-status">{c.status}</Badge>
            {pending && <Badge className="bg-amber-100 text-amber-800 animate-pulse" data-testid="course-editor-pending">AI writing…</Badge>}
            {c.ai?.status === "failed" && <Badge className="bg-red-100 text-red-700" title={c.ai.error}>AI failed</Badge>}
          </div>
          <DialogTitle className="sr-only">Edit course</DialogTitle>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-4">
          <div className="sm:col-span-2 space-y-1"><Label className="text-xs">Short description</Label><Input value={c.blurb || ""} onChange={e => set({ blurb: e.target.value })} className="h-8 text-sm" data-testid="course-editor-blurb" /></div>
          <div className="space-y-1"><Label className="text-xs">Pass mark %</Label><Input type="number" value={c.pass_mark} onChange={e => set({ pass_mark: Number(e.target.value) })} className="h-8 text-sm" data-testid="course-editor-pass-mark" /></div>
          <div className="space-y-1"><Label className="text-xs">Valid (months, 0 = no expiry)</Label><Input type="number" value={c.validity_months} onChange={e => set({ validity_months: Number(e.target.value) })} className="h-8 text-sm" data-testid="course-editor-validity" /></div>
        </div>

        <div className="flex gap-1 border-b border-border">
          {[["modules", `Modules (${c.modules.length})`], ["quiz", `Quiz (${c.quiz.length})`]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setTab(k)} className={`px-3 py-1.5 text-xs font-medium border-b-2 -mb-px ${tab === k ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`} data-testid={`course-editor-tab-${k}`}>{l}</button>
          ))}
        </div>

        {tab === "modules" && (
          <div className="space-y-3" data-testid="course-editor-modules">
            {c.modules.map((m, i) => (
              <div key={m.id} className="border border-border rounded-md p-3 space-y-2" data-testid={`course-module-${i}`}>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-semibold text-muted-foreground w-6">{i + 1}.</span>
                  <Input value={m.title} onChange={e => setM(i, { title: e.target.value })} placeholder="Module title" className="h-8 text-sm font-medium" data-testid={`course-module-title-${i}`} />
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" onClick={() => set({ modules: c.modules.filter((_, j) => j !== i) })} data-testid={`course-module-del-${i}`}><Trash size={14} /></Button>
                </div>
                <Textarea value={m.content} onChange={e => setM(i, { content: e.target.value })} rows={6} className="text-sm" data-testid={`course-module-content-${i}`} />
                <div className="space-y-1">
                  <Label className="text-[11px]">Key points (one per line)</Label>
                  <Textarea value={(m.key_points || []).join("\n")} onChange={e => setM(i, { key_points: e.target.value.split("\n") })} rows={3} className="text-xs" data-testid={`course-module-keypoints-${i}`} />
                </div>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => set({ modules: [...c.modules, blankM()] })} data-testid="course-module-add"><Plus size={14} className="mr-1" /> Add module</Button>
          </div>
        )}

        {tab === "quiz" && (
          <div className="space-y-3" data-testid="course-editor-quiz">
            {c.quiz.map((q, i) => (
              <div key={q.id} className="border border-border rounded-md p-3 space-y-2" data-testid={`course-q-${i}`}>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-semibold text-muted-foreground w-6">Q{i + 1}</span>
                  <Input value={q.question} onChange={e => setQ(i, { question: e.target.value })} placeholder="Question" className="h-8 text-sm" data-testid={`course-q-text-${i}`} />
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" onClick={() => set({ quiz: c.quiz.filter((_, j) => j !== i) })} data-testid={`course-q-del-${i}`}><Trash size={14} /></Button>
                </div>
                <div className="grid sm:grid-cols-2 gap-1.5 pl-8">
                  {q.options.map((o, k) => (
                    <div key={k} className="flex items-center gap-1.5">
                      <button type="button" title="Mark as the correct answer" onClick={() => setQ(i, { answer: k })} className={`h-6 w-6 rounded-full border flex items-center justify-center shrink-0 ${q.answer === k ? "bg-emerald-600 border-emerald-600 text-white" : "border-border text-transparent hover:text-muted-foreground"}`} data-testid={`course-q-correct-${i}-${k}`}><CheckCircle size={14} weight="bold" /></button>
                      <Input value={o} onChange={e => setQ(i, { options: q.options.map((x, j) => (j === k ? e.target.value : x)) })} className={`h-7 text-xs ${q.answer === k ? "border-emerald-400" : ""}`} data-testid={`course-q-opt-${i}-${k}`} />
                    </div>
                  ))}
                </div>
                <Input value={q.explanation || ""} onChange={e => setQ(i, { explanation: e.target.value })} placeholder="Why that's the right answer (shown after the quiz)" className="h-7 text-xs ml-8 w-[calc(100%-2rem)]" data-testid={`course-q-expl-${i}`} />
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => set({ quiz: [...c.quiz, blankQ()] })} data-testid="course-q-add"><Plus size={14} className="mr-1" /> Add question</Button>
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="ghost" onClick={regenerate} disabled={pending} data-testid="course-regenerate-btn"><ArrowsClockwise size={14} className="mr-1" /> Rewrite with AI</Button>
          <div className="flex-1" />
          <Button variant="outline" onClick={() => save()} disabled={busy || pending} data-testid="course-save-btn">{dirty ? "Save changes" : "Save"}</Button>
          {c.status !== "published" && <Button onClick={() => save("published")} disabled={busy || pending} data-testid="course-publish-btn">Publish</Button>}
          {c.status === "published" && <Button variant="outline" onClick={() => save("archived")} disabled={busy} data-testid="course-archive-btn">Archive</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
